import React from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { useToast } from 'containers/ToastManager'
import { ModalInjectedProps } from 'containers/ModalManager'

import Modal from 'components/Modal'
import Input from 'components/Input'
import Form from 'components/Form'
import Button from 'components/Button'
import ApolloError from 'components/ApolloError'

import { useUpdateWorkspaceMutation } from 'gqlTypes'

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("The workspace name can't be blank"),
})

interface UpdateWorkspaceNameModalProps extends ModalInjectedProps {
  id: string
  name: string
}

/*
 * TODO: https://github.com/sketch-hq/Cloud/issues/9295
 *
 * This component will be removed once we open `cloud-redesign-mvp` to everyone
 * if you need to make changes on this component please check with the `proj-cloud-redesign`
 * team or `team-onboarding`
 */
export const UpdateWorkspaceNameModal: React.FC<UpdateWorkspaceNameModalProps> = props => {
  const { name, id, hideModal } = props
  const { showToast } = useToast()

  const [updateWorkspace, { error }] = useUpdateWorkspaceMutation({
    onCompleted: () => {
      props.hideModal()
      showToast(`Workspace renamed`)
    },
    onError: 'show-toast',
  })

  return (
    <Modal title="Rename Workspace" onCancel={hideModal}>
      <Formik
        initialValues={{ name }}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          const { name } = validationSchema.cast(values)

          if (name === props.name) {
            actions.setSubmitting(false)
            props.hideModal()

            return
          }

          await updateWorkspace({
            variables: {
              input: {
                identifier: id,
                name,
              },
            },
          })

          actions.setSubmitting(false)
        }}>
        {({
          values,
          handleBlur,
          handleChange,
          isSubmitting,
          errors,
          touched,
        }) => (
          <Form>
            <Modal.Body>
              <Form.Field
                name="name"
                label="Name"
                errorText={touched.name ? errors.name : undefined}>
                <Input
                  name="name"
                  type="text"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Form.Field>
              {error && <ApolloError error={error} />}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                type="button"
                onClick={props.hideModal}
                disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                loading={isSubmitting}
                data-testid="rename-button">
                Rename
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}
